import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type {
  AutocompleteGroupedOption,
  UseAutocompleteProps,
} from '@mui/base';
import { useAutocomplete } from '@mui/base';
import { useId } from '@radix-ui/react-id';
import React from 'react';
import { match } from 'ts-pattern';
import { Badge } from '../Badge';
import { IconButton } from '../IconButton';
import {
  Popover,
  PopoverAnchor,
  PopoverArrow,
  PopoverContent,
} from '../Popover';
import { TextField } from '../TextField/TextField';
import { List, ListItem } from './List';

type Props<
  T,
  Multiple extends boolean | undefined = undefined,
  DisableClearable extends boolean | undefined = undefined,
  FreeSolo extends boolean | undefined = undefined
> = UseAutocompleteProps<T, Multiple, DisableClearable, FreeSolo> & {
  readonly label: React.ReactNode;
};

export function Autocomplete<
  T,
  Multiple extends boolean | undefined = undefined,
  DisableClearable extends boolean | undefined = undefined,
  FreeSolo extends boolean | undefined = undefined
>(props: Props<T, Multiple, DisableClearable, FreeSolo>) {
  const {
    label,
    groupBy,
    /* TODO: implement correct types to props */
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    getOptionLabel = (option) => (option as any).label ?? option,
    openOnFocus = true,
  } = props;
  const id = useId();

  const {
    getRootProps,
    getInputLabelProps,
    getInputProps,
    getListboxProps,
    getOptionProps,
    getTagProps,
    getPopupIndicatorProps,
    groupedOptions,
    popupOpen,
    value,
    setAnchorEl,
    anchorEl,
  } = useAutocomplete({
    id,
    openOnFocus,
    ...props,
  });

  return (
    <>
      <TextField
        label={label}
        shrinkLabel={Array.isArray(value) && value.length > 0}
        RootProps={{ ...getRootProps() }}
        inputProps={{
          ...getInputProps(),
        }}
        ref={setAnchorEl}
        endAdornment={
          <IconButton
            /* TODO: implement correct types to props */
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            {...(getPopupIndicatorProps() as any)}
            css={{ height: '1.5rem', width: '1.5rem', padding: '0' }}
            color="grey"
            variant={'noShape'}
            static>
            <FontAwesomeIcon
              icon={['fas', 'chevron-down']}
              fixedWidth
              rotation={popupOpen ? 180 : undefined}
              style={{
                transform: '',
                transition: 'transform 300ms cubic-bezier(0.65, 0, 0.35, 1)',
              }}
            />
          </IconButton>
        }
        LabelProps={{ ...getInputLabelProps() }}>
        {/* This part is for Multiple Options only */}
        {Array.isArray(value) &&
          value.map((option, index) => (
            <Badge {...getTagProps({ index })}>{getOptionLabel(option)}</Badge>
          ))}
      </TextField>
      <Popover open={popupOpen}>
        <PopoverAnchor virtualRef={{ current: anchorEl }} />
        <PopoverContent
          id={`${id}-popover`}
          data-testid="autocomplete-popover"
          sideOffset={8}>
          <List
            {...getListboxProps()}
            css={{ maxHeight: 256, overflowY: 'auto' }}
            interactive>
            {match(groupedOptions.length)
              .when(
                (v) => v > 0,
                () => (
                  <>
                    {groupedOptions.map((option, index) => {
                      if (isGroup(option, groupBy)) {
                        // TODO: Implement grouped options
                        return null;
                      }

                      return (
                        <ListItem
                          /* TODO: implement correct types to props */
                          // eslint-disable-next-line @typescript-eslint/no-explicit-any
                          {...(getOptionProps as any)({ option, index })}>
                          {getOptionLabel(option)}
                        </ListItem>
                      );
                    })}
                  </>
                )
              )
              .otherwise(() => (
                <>
                  <ListItem>No options found</ListItem>
                </>
              ))}
          </List>
          <PopoverArrow />
        </PopoverContent>
      </Popover>
    </>
  );
}

function isGroup<T>(
  option: T | AutocompleteGroupedOption<T>,
  groupBy: unknown
): option is AutocompleteGroupedOption<T> {
  return Boolean(groupBy);
}
